import authService from '../../services/auth.service';
import { getTenantFromUrl } from '../../services/tenant.service';
import { debugLog, debugWarn } from '../../utils/debugLog';

/**
 * Extract tenant from URL with security validation
 */
export const extractTenantFromUrl = () => {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;
  const search = window.location.search;
  const storedTenant = authService.getTenant();

  // Get tenant from URL with validation
  const { tenant, warning } = getTenantFromUrl(hostname, search, storedTenant);

  // Log security warning if mismatch detected
  if (warning) {
    debugWarn(warning);
  }

  return tenant;
};

/**
 * Get tenant alias string from tenant object or string
 */
export const getTenantAlias = tenant => {
  if (tenant) {
    return typeof tenant === 'string'
      ? tenant
      : tenant.alias || tenant.tenant_id || null;
  }
  return null;
};

/**
 * Load and validate tenant from URL and localStorage
 * Returns validated tenant alias and updates localStorage if needed
 */
export const validateAndSyncTenant = (tenantFromUrl, cachedTenant) => {
  let validatedTenant = null;

  if (tenantFromUrl) {
    // URL tenant is authoritative
    validatedTenant = tenantFromUrl;
    // Store in localStorage if different from cached
    if (
      !cachedTenant ||
      (typeof cachedTenant === 'string' ? cachedTenant : cachedTenant.alias) !==
        tenantFromUrl
    ) {
      const tenantToSave = {
        alias: tenantFromUrl,
        tenant_id: tenantFromUrl,
      };

      // Preserve adapters from cached tenant if available
      if (
        cachedTenant &&
        typeof cachedTenant === 'object' &&
        cachedTenant.adapters
      ) {
        tenantToSave.adapters = cachedTenant.adapters;
      }

      authService.setTenant(tenantToSave);
    }
  } else if (cachedTenant) {
    // No URL tenant, use cached
    validatedTenant =
      typeof cachedTenant === 'string'
        ? cachedTenant
        : cachedTenant.alias || cachedTenant.tenant_id;
  }

  return validatedTenant;
};

/**
 * Create tenant object from validated tenant and cached tenant
 */
export const createTenantObject = (validatedTenant, cachedTenant) => {
  if (!validatedTenant) return null;

  let tenantObj;
  if (cachedTenant && typeof cachedTenant === 'object') {
    // Use cached tenant
    tenantObj = { ...cachedTenant }; // Create a copy to avoid mutations

    debugLog('✅ Loaded tenant from localStorage:', {
      alias: tenantObj.alias || tenantObj.tenant_id,
      tenantKeys: Object.keys(tenantObj),
    });
  } else {
    // No cached tenant, create new object
    tenantObj = { alias: validatedTenant, tenant_id: validatedTenant };
  }

  return tenantObj;
};
